import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import './snake.css';
import LeaderboardNameModal from '../common/leaderboardNameModal';
import {
  getPlayerNameValidationMessage,
  getSavedPlayerName,
  submitGameHighscore,
} from '../../../services/leaderboardService';
const audioFile = require('./snakeTheme.mp3');

interface Position {
  x: number;
  y: number;
}

interface Food {
  x: number;
  y: number;
  type: 'normal' | 'bonus' | 'super';
  points: number;
  color: string;
}

interface GameState {
  score: number;
  level: number;
  gameStarted: boolean;
  gameOver: boolean;
  gameWon: boolean;
  isPaused: boolean;
  levelCompleted: boolean;
  speed: number;
  foodEaten: number;
  targetFood: number;
}

const DIRECTIONS = {
  UP: { x: 0, y: -1 },
  DOWN: { x: 0, y: 1 },
  LEFT: { x: -1, y: 0 },
  RIGHT: { x: 1, y: 0 }
};

const CELL_SIZE = 20;

interface SnakeGameProps {
  onClose?: () => void;
  onReload?: () => void;
  onLeaderboardUpdated?: () => void;
}

function SnakeGame({ onClose, onReload, onLeaderboardUpdated }: SnakeGameProps) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const lastTimeRef = useRef<number>(0);
  const audioRef = useRef(new Audio(audioFile));
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [showMobileControls, setShowMobileControls] = useState(false);
  const [gameState, setGameState] = useState<GameState>({
    score: 0,
    level: 1,
    gameStarted: false,
    gameOver: false,
    gameWon: false,
    isPaused: false,
    levelCompleted: false,
    speed: 150,
    foodEaten: 0,
    targetFood: 5,
  });
  
  const [highScore, setHighScore] = useState(() => {
    return parseInt(localStorage.getItem('snakeHighScore') || '0');
  });
  const [showNameModal, setShowNameModal] = useState(false);
  const [scoreNameError, setScoreNameError] = useState<string | null>(null);
  const [isSubmittingScore, setIsSubmittingScore] = useState(false);
  const [nameDefault, setNameDefault] = useState(getSavedPlayerName());
  const leaderboardSubmittedRef = useRef(false);

  // Responsive canvas size
  const getCanvasSize = () => {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const maxWidth = Math.min(windowWidth * 0.9, 800);
    const maxHeight = Math.min(windowHeight * 0.8, 600);
    
    // Make sure dimensions are divisible by CELL_SIZE
    const width = Math.floor(maxWidth / CELL_SIZE) * CELL_SIZE;
    const height = Math.floor(maxHeight / CELL_SIZE) * CELL_SIZE;
    
    return { width: Math.max(width, 400), height: Math.max(height, 300) };
  };

  const [canvasSize, setCanvasSize] = useState(getCanvasSize());
  const [gridWidth, setGridWidth] = useState(Math.floor(canvasSize.width / CELL_SIZE));
  const [gridHeight, setGridHeight] = useState(Math.floor(canvasSize.height / CELL_SIZE));

  // Game objects
  const [snake, setSnake] = useState<Position[]>([{ x: 5, y: 5 }]);
  const [direction, setDirection] = useState(DIRECTIONS.RIGHT);
  const [nextDirection, setNextDirection] = useState(DIRECTIONS.RIGHT);
  const [food, setFood] = useState<Food[]>([]);

  const setDirectionFromButton = useCallback((newDirection: { x: number; y: number }) => {
    if (!gameState.gameStarted || gameState.gameOver || gameState.gameWon) return;
    setNextDirection(newDirection);
  }, [gameState.gameStarted, gameState.gameOver, gameState.gameWon]);

  // Level configurations wrapped in useMemo
  const levelConfigs = useMemo(() => [
    { speed: 150, targetFood: 5, bonusChance: 0.1, superChance: 0.02 },
    { speed: 130, targetFood: 8, bonusChance: 0.15, superChance: 0.03 },
    { speed: 110, targetFood: 12, bonusChance: 0.2, superChance: 0.04 },
    { speed: 90, targetFood: 15, bonusChance: 0.25, superChance: 0.05 },
    { speed: 80, targetFood: 18, bonusChance: 0.3, superChance: 0.06 },
    { speed: 70, targetFood: 22, bonusChance: 0.35, superChance: 0.07 },
    { speed: 60, targetFood: 25, bonusChance: 0.4, superChance: 0.08 },
    { speed: 50, targetFood: 30, bonusChance: 0.45, superChance: 0.09 },
    { speed: 40, targetFood: 35, bonusChance: 0.5, superChance: 0.1 },
    { speed: 30, targetFood: 40, bonusChance: 0.6, superChance: 0.12 },
  ], []);

  const generateFood = useCallback(() => {
    const config = levelConfigs[gameState.level - 1];
    let newFood: Food;
    let validPosition = false;
    
    // Try to find a valid position (not on snake)
    do {
      const x = Math.floor(Math.random() * gridWidth);
      const y = Math.floor(Math.random() * gridHeight);
      validPosition = !snake.some(segment => segment.x === x && segment.y === y) &&
                     !food.some(f => f.x === x && f.y === y);
      
      if (validPosition) {
        // Determine food type based on level configuration
        const rand = Math.random();
        let type: 'normal' | 'bonus' | 'super' = 'normal';
        let points = 10;
        let color = '#FF6B6B';
        
        if (rand < config.superChance) {
          type = 'super';
          points = 100;
          color = '#FFD700';
        } else if (rand < config.bonusChance) {
          type = 'bonus';
          points = 25;
          color = '#4ECDC4';
        }
        
        newFood = { x, y, type, points, color };
      }
    } while (!validPosition && snake.length < gridWidth * gridHeight - 1);
    
    if (validPosition) {
      setFood(prev => [...prev, newFood!]);
    }
  }, [snake, food, gridWidth, gridHeight, gameState.level, levelConfigs]);

  const moveSnake = useCallback(() => {
    setSnake(prevSnake => {
      const newSnake = [...prevSnake];
      const head = { ...newSnake[0] };
      
      // Use next direction if it's not opposite to current direction
      let currentDirection = direction;
      if (
        !(nextDirection.x === -direction.x && nextDirection.y === -direction.y) &&
        (nextDirection.x !== 0 || nextDirection.y !== 0)
      ) {
        currentDirection = nextDirection;
        setDirection(nextDirection);
      }
      
      head.x += currentDirection.x;
      head.y += currentDirection.y;
      
      // Wall collision
      if (head.x < 0 || head.x >= gridWidth || head.y < 0 || head.y >= gridHeight) {
        setGameState(prev => ({ ...prev, gameOver: true }));
        return prevSnake;
      }
      
      // Self collision
      if (newSnake.some(segment => segment.x === head.x && segment.y === head.y)) {
        setGameState(prev => ({ ...prev, gameOver: true }));
        return prevSnake;
      }
      
      newSnake.unshift(head);
      
      // Check food collision
      const eatenFood = food.find(f => f.x === head.x && f.y === head.y);
      if (eatenFood) {
        // Remove eaten food
        setFood(prev => prev.filter(f => f !== eatenFood));
        
        // Update score and game state
        setGameState(prev => {
          const newScore = prev.score + eatenFood.points;
          const newFoodEaten = prev.foodEaten + 1;
          
          if (newScore > highScore) {
            setHighScore(newScore);
            localStorage.setItem('snakeHighScore', newScore.toString());
          }
          
          return {
            ...prev,
            score: newScore,
            foodEaten: newFoodEaten
          };
        });
        
        // Don't remove tail segment (snake grows)
      } else {
        // Remove tail segment (snake moves)
        newSnake.pop();
      }
      
      return newSnake;
    });
  }, [direction, nextDirection, food, gridWidth, gridHeight, highScore]);

  const nextLevel = useCallback(() => {
    if (gameState.level < 10) {
      const newLevel = gameState.level + 1;
      const config = levelConfigs[newLevel - 1];
      
      setGameState(prev => ({ 
        ...prev, 
        level: newLevel,
        speed: config.speed,
        targetFood: config.targetFood,
        foodEaten: 0,
        levelCompleted: false
      }));
      
      // Reset snake and food for new level
      setSnake([{ x: Math.floor(gridWidth / 2), y: Math.floor(gridHeight / 2) }]);
      setDirection(DIRECTIONS.RIGHT);
      setNextDirection(DIRECTIONS.RIGHT);
      setFood([]);
    } else {
      setGameState(prev => ({ ...prev, gameWon: true }));
    }
  }, [gameState.level, levelConfigs, gridWidth, gridHeight]);

  const proceedToNextLevel = useCallback(() => {
    nextLevel();
  }, [nextLevel]);

  const gameLoop = useCallback((currentTime: number) => {
    if (!gameState.gameStarted || gameState.gameOver || gameState.gameWon || gameState.isPaused) {
      return;
    }

    if (currentTime - lastTimeRef.current >= gameState.speed) {
      moveSnake();
      lastTimeRef.current = currentTime;
      
      // Generate food if needed
      if (food.length === 0) {
        generateFood();
      }
      
      // Check level completion
      if (gameState.foodEaten >= gameState.targetFood && !gameState.levelCompleted) {
        setGameState(prev => ({ ...prev, levelCompleted: true }));
      }
    }

    animationRef.current = requestAnimationFrame(gameLoop);
  }, [gameState, moveSnake, generateFood, food.length]);

  const drawGame = useCallback(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas with gradient background
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#1a1a2e');
    gradient.addColorStop(1, '#16213e');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw grid
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    for (let x = 0; x <= canvas.width; x += CELL_SIZE) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }
    for (let y = 0; y <= canvas.height; y += CELL_SIZE) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }

    // Draw food
    food.forEach(f => {
      const x = f.x * CELL_SIZE;
      const y = f.y * CELL_SIZE;
      
      if (f.type === 'super') {
        // Animated super food
        const time = Date.now() / 200;
        const scale = 0.8 + 0.2 * Math.sin(time);
        const size = CELL_SIZE * scale;
        const offset = (CELL_SIZE - size) / 2;
        
        ctx.fillStyle = f.color;
        ctx.shadowColor = f.color;
        ctx.shadowBlur = 10;
        ctx.fillRect(x + offset, y + offset, size, size);
        ctx.shadowBlur = 0;
      } else {
        ctx.fillStyle = f.color;
        if (f.type === 'bonus') {
          ctx.shadowColor = f.color;
          ctx.shadowBlur = 5;
        }
        ctx.fillRect(x + 2, y + 2, CELL_SIZE - 4, CELL_SIZE - 4);
        ctx.shadowBlur = 0;
      }
    });

    // Draw snake
    snake.forEach((segment, index) => {
      const x = segment.x * CELL_SIZE;
      const y = segment.y * CELL_SIZE;
      
      if (index === 0) {
        // Snake head
        const headGradient = ctx.createRadialGradient(
          x + CELL_SIZE/2, y + CELL_SIZE/2, 0,
          x + CELL_SIZE/2, y + CELL_SIZE/2, CELL_SIZE/2
        );
        headGradient.addColorStop(0, '#4ECDC4');
        headGradient.addColorStop(1, '#44A08D');
        ctx.fillStyle = headGradient;
        ctx.fillRect(x + 1, y + 1, CELL_SIZE - 2, CELL_SIZE - 2);
        
        // Eyes
        ctx.fillStyle = '#FFFFFF';
        const eyeSize = 3;
        ctx.fillRect(x + 6, y + 5, eyeSize, eyeSize);
        ctx.fillRect(x + 11, y + 5, eyeSize, eyeSize);
      } else {
        // Snake body
        const bodyGradient = ctx.createLinearGradient(x, y, x + CELL_SIZE, y + CELL_SIZE);
        const intensity = Math.max(0.3, 1 - index * 0.1);
        bodyGradient.addColorStop(0, `rgba(78, 205, 196, ${intensity})`);
        bodyGradient.addColorStop(1, `rgba(68, 160, 141, ${intensity})`);
        ctx.fillStyle = bodyGradient;
        ctx.fillRect(x + 2, y + 2, CELL_SIZE - 4, CELL_SIZE - 4);
      }
    });

    // Draw UI
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 18px Arial';
    ctx.fillText(t('gamesCommon.scoreWithValue', { score: gameState.score }), 10, 25);
    ctx.fillText(t('gamesCommon.levelWithValue', { level: gameState.level }), 10, 50);
    ctx.fillText(t('snakeGame.foodWithValue', { current: gameState.foodEaten, total: gameState.targetFood }), 10, 75);
    ctx.fillText(t('gamesCommon.highScoreWithValue', { score: highScore }), canvas.width - 150, 25);
    
    // Progress bar
    const progressWidth = 200;
    const progressHeight = 10;
    const progressX = canvas.width - progressWidth - 10;
    const progressY = 35;
    const progress = gameState.foodEaten / gameState.targetFood;
    
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 2;
    ctx.strokeRect(progressX, progressY, progressWidth, progressHeight);
    
    const fillGradient = ctx.createLinearGradient(progressX, progressY, progressX + progressWidth, progressY);
    fillGradient.addColorStop(0, '#4ECDC4');
    fillGradient.addColorStop(1, '#44A08D');
    ctx.fillStyle = fillGradient;
    ctx.fillRect(progressX + 1, progressY + 1, (progressWidth - 2) * progress, progressHeight - 2);
  }, [snake, food, gameState, highScore, t]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!gameState.gameStarted) return;
      
      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          e.preventDefault();
          setNextDirection(DIRECTIONS.UP);
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          e.preventDefault();
          setNextDirection(DIRECTIONS.DOWN);
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          e.preventDefault();
          setNextDirection(DIRECTIONS.LEFT);
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          e.preventDefault();
          setNextDirection(DIRECTIONS.RIGHT);
          break;
        case ' ':
          e.preventDefault();
          setGameState(prev => ({ ...prev, isPaused: !prev.isPaused }));
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState.gameStarted]);

  // Touch controls for mobile
  const handleTouchStart = useRef<{ x: number; y: number } | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    handleTouchStart.current = { x: touch.clientX, y: touch.clientY };
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (!handleTouchStart.current || !gameState.gameStarted) return;
    
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - handleTouchStart.current.x;
    const deltaY = touch.clientY - handleTouchStart.current.y;
    
    const minSwipeDistance = 50;
    
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      if (Math.abs(deltaX) > minSwipeDistance) {
        setNextDirection(deltaX > 0 ? DIRECTIONS.RIGHT : DIRECTIONS.LEFT);
      }
    } else {
      if (Math.abs(deltaY) > minSwipeDistance) {
        setNextDirection(deltaY > 0 ? DIRECTIONS.DOWN : DIRECTIONS.UP);
      }
    }
    
    handleTouchStart.current = null;
  };

  // Audio setup
  useEffect(() => {
    audioRef.current.loop = true;
    const audio = audioRef.current;
    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, []);

  useEffect(() => {
    if (isPlaying) {
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  // Game loop
  useEffect(() => {
    if (gameState.gameStarted && !gameState.gameOver && !gameState.gameWon && !gameState.isPaused) {
      animationRef.current = requestAnimationFrame(gameLoop);
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [gameLoop, gameState]);

  // Draw loop
  useEffect(() => {
    const draw = () => {
      drawGame();
      requestAnimationFrame(draw);
    };
    draw();
  }, [drawGame]);

  // Window resize handler
  useEffect(() => {
    const handleResize = () => {
      const newSize = getCanvasSize();
      setCanvasSize(newSize);
      setGridWidth(Math.floor(newSize.width / CELL_SIZE));
      setGridHeight(Math.floor(newSize.height / CELL_SIZE));
      setShowMobileControls(window.innerWidth <= 900);
    };

    setShowMobileControls(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize food
  useEffect(() => {
    if (gameState.gameStarted && food.length === 0) {
      generateFood();
    }
  }, [gameState.gameStarted, food.length, generateFood]);

  useEffect(() => {
    if (!gameState.gameStarted || leaderboardSubmittedRef.current) {
      return;
    }

    const finishedRun = gameState.gameOver || gameState.gameWon;
    if (!finishedRun || gameState.score <= 0) {
      return;
    }

    setNameDefault(getSavedPlayerName());
    setScoreNameError(null);
    setShowNameModal(true);
  }, [gameState.gameOver, gameState.gameStarted, gameState.gameWon, gameState.score, onLeaderboardUpdated]);

  const handleScoreSubmit = useCallback(async (name: string) => {
    const error = getPlayerNameValidationMessage(name);
    if (error) {
      setScoreNameError(error);
      return;
    }

    setIsSubmittingScore(true);
    setScoreNameError(null);
    try {
      const submitted = await submitGameHighscore('snake', name, gameState.score);
      if (submitted) {
        leaderboardSubmittedRef.current = true;
        setShowNameModal(false);
        onLeaderboardUpdated?.();
      } else {
        setScoreNameError('leaderboardModal.errorOnlineUnavailable');
      }
    } catch (submitError) {
      setScoreNameError('leaderboardModal.errorSubmit');
      console.error('Snake leaderboard submit failed:', submitError);
    } finally {
      setIsSubmittingScore(false);
    }
  }, [gameState.score, onLeaderboardUpdated]);

  const handleScoreSkip = useCallback(() => {
    leaderboardSubmittedRef.current = true;
    setScoreNameError(null);
    setShowNameModal(false);
  }, []);

  const startGame = () => {
    leaderboardSubmittedRef.current = false;
    setShowNameModal(false);
    setScoreNameError(null);
    const config = levelConfigs[0];
    setGameState({
      score: 0,
      level: 1,
      gameStarted: true,
      gameOver: false,
      gameWon: false,
      isPaused: false,
      levelCompleted: false,
      speed: config.speed,
      foodEaten: 0,
      targetFood: config.targetFood,
    });
    setSnake([{ x: Math.floor(gridWidth / 2), y: Math.floor(gridHeight / 2) }]);
    setDirection(DIRECTIONS.RIGHT);
    setNextDirection(DIRECTIONS.RIGHT);
    setFood([]);
  };

  const restartGame = () => {
    leaderboardSubmittedRef.current = false;
    startGame();
  };

  const toggleMusic = () => {
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="snake-container">
      <div className="game-header">
        <div className="controls left">
          <button onClick={toggleMusic} className="music-btn">
            {isPlaying ? '🔊' : '🔇'} {t('gamesCommon.music')}
          </button>
        </div>
        <div className="controls right">
          {onClose && (
            <button onClick={onClose} className="close-button">
              ✕
            </button>
          )}
        </div>
      </div>

      {!gameState.gameStarted ? (
        <div className="start-screen">
          <h2>{t('snakeGame.title')}</h2>
          <p>{t('snakeGame.startKeyboard')}</p>
          <p>{t('snakeGame.startMobile')}</p>
          <p>{t('snakeGame.startPause')}</p>
          <p>{t('snakeGame.startObjective')}</p>
          <div className="food-types">
            <div className="food-info">
              <div className="food-sample normal"></div>
              <span>{t('snakeGame.normalFood')}</span>
            </div>
            <div className="food-info">
              <div className="food-sample bonus"></div>
              <span>{t('snakeGame.bonusFood')}</span>
            </div>
            <div className="food-info">
              <div className="food-sample super"></div>
              <span>{t('snakeGame.superFood')}</span>
            </div>
          </div>
          <button onClick={startGame} className="start-btn">{t('gamesCommon.startGame')}</button>
        </div>
      ) : (
        <>
          <canvas
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            className="game-canvas"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          />

          {showMobileControls && (
            <div className="snake-mobile-controls">
              <div className="snake-mobile-row">
                <button className="snake-mobile-btn" onClick={() => setDirectionFromButton(DIRECTIONS.UP)}>↑</button>
              </div>
              <div className="snake-mobile-row">
                <button className="snake-mobile-btn" onClick={() => setDirectionFromButton(DIRECTIONS.LEFT)}>←</button>
                <button
                  className="snake-mobile-btn snake-mobile-btn-pause"
                  onClick={() => setGameState(prev => ({ ...prev, isPaused: !prev.isPaused }))}
                >
                  {gameState.isPaused ? '▶' : 'II'}
                </button>
                <button className="snake-mobile-btn" onClick={() => setDirectionFromButton(DIRECTIONS.RIGHT)}>→</button>
              </div>
              <div className="snake-mobile-row">
                <button className="snake-mobile-btn" onClick={() => setDirectionFromButton(DIRECTIONS.DOWN)}>↓</button>
              </div>
            </div>
          )}
          
          {gameState.isPaused && (
            <div className="pause-overlay">
              <h2>{t('gamesCommon.paused')}</h2>
              <p>{t('snakeGame.pressSpaceToContinue')}</p>
            </div>
          )}
          
          {gameState.levelCompleted && (
            <div className="level-completed-overlay">
              <h2>{t('snakeGame.levelComplete', { level: gameState.level })}</h2>
              <p>{t('gamesCommon.scoreWithValue', { score: gameState.score })}</p>
              <p>{t('snakeGame.snakeLength', { length: snake.length })}</p>
              {gameState.level < 10 ? (
                <button onClick={proceedToNextLevel} className="next-level-btn">
                  {t('gamesCommon.nextLevel')}
                </button>
              ) : (
                <button onClick={() => setGameState(prev => ({ ...prev, gameWon: true }))} className="next-level-btn">
                  {t('snakeGame.finishGame')}
                </button>
              )}
            </div>
          )}
          
          {gameState.gameOver && (
            <div className="game-over-overlay">
              <h2>{t('gamesCommon.gameOver')}</h2>
              <p>{t('gamesCommon.finalScore', { score: gameState.score })}</p>
              <p>{t('gamesCommon.levelReached', { level: gameState.level })}</p>
              <p>{t('snakeGame.snakeLength', { length: snake.length })}</p>
              {gameState.score === highScore && <p className="new-high-score">{t('gamesCommon.newHighScore')}</p>}
              <div className="overlay-buttons">
                <button onClick={restartGame} className="restart-btn">{t('gamesCommon.playAgain')}</button>
                {onReload && <button onClick={onReload} className="restart-btn">{t('gamesCommon.restart')}</button>}
              </div>
            </div>
          )}
          
          {gameState.gameWon && (
            <div className="game-won-overlay">
              <h2>{t('gamesCommon.congratulations')}</h2>
              <p>{t('snakeGame.completedAllLevels')}</p>
              <p>{t('gamesCommon.finalScore', { score: gameState.score })}</p>
              <p>{t('snakeGame.finalSnakeLength', { length: snake.length })}</p>
              {gameState.score === highScore && <p className="new-high-score">{t('gamesCommon.newHighScore')}</p>}
              <div className="overlay-buttons">
                <button onClick={restartGame} className="restart-btn">{t('gamesCommon.playAgain')}</button>
                {onReload && <button onClick={onReload} className="restart-btn">{t('gamesCommon.restart')}</button>}
              </div>
            </div>
          )}
        </>
      )}
      <LeaderboardNameModal
        open={showNameModal}
        score={gameState.score}
        defaultName={nameDefault}
        isSubmitting={isSubmittingScore}
        error={scoreNameError}
        onSubmit={handleScoreSubmit}
        onCancel={handleScoreSkip}
      />
    </div>
  );
}

export default SnakeGame;